import React, { useEffect, useState } from "react";
import api, { fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Calculator, AlertTriangle } from "lucide-react";
import { fmtGbp } from "@/lib/constants";

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const hrs = (budget, rate) => (rate ? r2(budget / rate - 2) : null);

export function BudgetPanel({ wp, engagement, locked, onSaved }) {
    const isPrepAudit = engagement.engagement_type === "prep_audit";
    const [ceFee, setCeFee] = useState(wp.data?.budget?.ce_fee ?? "");
    const [team, setTeam] = useState(null);

    useEffect(() => { api.get(`/engagements/${engagement.id}/team`).then((r) => setTeam(r.data)).catch(() => setTeam({ members: [] })); }, [engagement.id]);

    const fee = Number(ceFee) || 0;
    const preparer = team?.preparer;
    const auditor = team?.auditor;

    // single-role (default) figures
    const budget = r2(fee / 5);
    const timeBudget = hrs(budget, preparer?.hourly_rate);
    // prep + audit figures (fee split in half)
    const half = r2(fee / 2);
    const pBudget = r2(half / 5);
    const aBudget = r2(half / 5);
    const pTime = hrs(pBudget, preparer?.hourly_rate);
    const aTime = hrs(aBudget, auditor?.hourly_rate);

    const dirty = fee !== (wp.data?.budget?.ce_fee ?? 0);
    const save = async () => {
        try {
            const snapshot = isPrepAudit
                ? { ce_fee: fee, half, preparer_budget: pBudget, preparer_time: pTime, auditor_budget: aBudget, auditor_time: aTime }
                : { ce_fee: fee, budget, time_budget: timeBudget };
            await api.patch(`/working-papers/${wp.id}/data`, { budget: snapshot });
            toast.success("Fee & time budget saved");
            onSaved?.();
        } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };

    const inp = "border border-input rounded-sm px-2 py-1.5 text-sm font-num bg-white w-full disabled:bg-slate-50";

    return (
        <div className="bg-white border-2 border-brand/40 rounded-sm mb-5" data-testid="budget-panel">
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
                <Calculator className="w-4 h-4 text-brand" />
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-display font-semibold">Fee &amp; time budget · CE fee ÷ 5 = budget · hours = budget ÷ hourly rate − 2</span>
            </div>
            <div className="p-4">
                <div className="grid grid-cols-4 gap-4 items-end mb-4">
                    <L label="CE Fee (£)"><input data-testid="budget-ce-fee" type="number" disabled={locked} value={ceFee} onChange={(e) => setCeFee(e.target.value)} className={inp} /></L>
                    {isPrepAudit && <Out label="Fee split (½ each)" v={half} testid="budget-half" />}
                </div>

                {!isPrepAudit ? (
                    <div className="grid grid-cols-3 gap-8 items-end">
                        <Out label="Budget (£)" v={budget} testid="budget-amount" />
                        <RateOut label="Preparer hourly rate" member={preparer} testid="budget-rate" />
                        <TimeOut label="Time budget (hrs, −2)" v={timeBudget} testid="budget-hours" missing={!preparer?.hourly_rate} />
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-6">
                        <RoleBlock title="Preparer" member={preparer} budget={pBudget} time={pTime} slot="preparer" />
                        <RoleBlock title="Auditor" member={auditor} budget={aBudget} time={aTime} slot="auditor" />
                    </div>
                )}

                {!locked && (
                    <div className="flex justify-end mt-4">
                        <button data-testid="budget-save-btn" onClick={save} disabled={!fee} className="bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-1.5 rounded-sm disabled:opacity-40">{dirty ? "Save budget" : "Saved ✓"}</button>
                    </div>
                )}
            </div>
        </div>
    );
}

function RoleBlock({ title, member, budget, time, slot }) {
    return (
        <div className="border border-border rounded-sm p-3" data-testid={`budget-role-${slot}`}>
            <div className="text-[11px] uppercase tracking-widest text-brand font-display font-bold mb-2">{title}{member ? ` · ${member.name}` : ""}</div>
            <div className="grid grid-cols-3 gap-4">
                <Out label="Budget (£)" v={budget} testid={`budget-${slot}-amount`} />
                <RateOut label="Hourly rate" member={member} testid={`budget-${slot}-rate`} />
                <TimeOut label="Time budget (hrs, −2)" v={time} testid={`budget-${slot}-hours`} missing={!member?.hourly_rate} />
            </div>
        </div>
    );
}

function L({ label, children }) {
    return <div><label className="text-[10px] uppercase tracking-wider text-muted-foreground font-display block mb-1 leading-tight">{label}</label>{children}</div>;
}
function Out({ label, v, testid }) {
    return <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-display">{label}</div><div className="font-display font-bold text-xl font-num" data-testid={testid}>{v ? fmtGbp(v) : "—"}</div></div>;
}
function RateOut({ label, member, testid }) {
    const rate = member?.hourly_rate;
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-display">{label}</div>
            {rate ? <div className="font-display font-bold text-xl font-num" data-testid={testid}>{fmtGbp(rate)}</div>
                : <div className="text-xs text-amber-700 flex items-center gap-1 mt-1" data-testid={testid}><AlertTriangle className="w-3.5 h-3.5" />{member ? "rate not set in profile" : "not assigned"}</div>}
        </div>
    );
}
function TimeOut({ label, v, testid, missing }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-display">{label}</div>
            {missing ? <div className="text-xs text-muted-foreground mt-1" data-testid={testid}>—</div>
                : <div className="font-display font-bold text-xl font-num" data-testid={testid}>{v == null ? "—" : `${v} hrs`}</div>}
        </div>
    );
}
